import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { fetchApi } from '../api/client';
import type { SystemUser } from '../types';

// Routed through the scoped func API (GET /staff → ClientFunctions.GetStaff) instead of a
// browser-side Dataverse read — closes the per-caller-scoping bypass described in the 2026-07-03
// supervision audit finding #1. The staff roster itself is intentionally NOT narrowed per-caller
// (reference data, not client-owned data — mirrors how GetDashboardStats treats org-wide figures).
export function useSystemUsers() {
  return useQuery({
    queryKey: ['system-users'],
    queryFn: () => fetchApi<SystemUser[]>('/staff'),
    staleTime: 1000 * 60 * 30, // 30 minutes - users don't change often
  });
}

/**
 * The staff who may be OFFERED as the reviewer on a new review — `GET /staff-reviewers`, a strict
 * subset of `/staff`.
 *
 * Use this ONLY for choosing a reviewer. It is deliberately NOT a staff roster: do not reach for it
 * to render an owner's name, to populate the bookkeeper picker, or to resolve the signed-in user.
 * Those all need the full roster (`useSystemUsers`), and narrowing them is the reverse defect —
 * a real person vanishing from a list they belong in. The split exists because one `/staff`
 * response feeds all four jobs; see `api/Services/IDataverseService.GetReviewerEligibleStaffAsync`.
 *
 * A separate query key, so the reviewer list and the roster cache independently and neither can be
 * served from the other's entry.
 */
export function useReviewerEligibleStaff() {
  return useQuery({
    queryKey: ['reviewer-eligible-staff'],
    queryFn: () => fetchApi<SystemUser[]>('/staff-reviewers'),
    staleTime: 1000 * 60 * 30, // 30 minutes — same cadence as the roster
  });
}

/**
 * Find the system user that matches the signed-in MSAL user
 * by matching email, Azure AD Object ID, domain name, or full name
 */
export function useCurrentSystemUser() {
  const { accounts } = useMsal();
  const { data: users, isLoading, error } = useSystemUsers();

  const currentAccount = accounts[0];

  // Extract object ID from localAccountId (may be in format "objectId.tenantId")
  const localAccountId = currentAccount?.localAccountId;
  const objectIdPart = localAccountId?.split('.')[0];

  // Try to match by multiple fields for robustness
  const currentUser = users?.find(user => {
    // Match by Azure AD Object ID (try both full ID and object ID part)
    if (user.azureactivedirectoryobjectid && objectIdPart) {
      if (user.azureactivedirectoryobjectid.toLowerCase() === objectIdPart.toLowerCase()) {
        return true;
      }
      if (user.azureactivedirectoryobjectid.toLowerCase() === localAccountId?.toLowerCase()) {
        return true;
      }
    }
    // Match by email (most reliable)
    if (user.internalemailaddress && currentAccount?.username) {
      if (user.internalemailaddress.toLowerCase() === currentAccount.username.toLowerCase()) {
        return true;
      }
    }
    // Match by domain name (UPN)
    if (user.domainname && currentAccount?.username) {
      if (user.domainname.toLowerCase() === currentAccount.username.toLowerCase()) {
        return true;
      }
    }
    // Match by full name as last resort
    if (user.fullname && currentAccount?.name) {
      if (user.fullname.toLowerCase() === currentAccount.name.toLowerCase()) {
        return true;
      }
    }
    return false;
  });

  return {
    data: currentUser,
    allUsers: users,
    isLoading,
    error,
  };
}
